import { X, AlertCircle, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { toast } from 'sonner';

interface NotificationOptions {
  title?: string;
  description?: string;
  duration?: number | null;
  onDismiss?: () => void;
}

type NotificationType = 'info' | 'success' | 'error' | 'warning';

interface TypeConfig {
  icon: React.ComponentType<{ className?: string }>;
  bgColor: string;
  borderColor: string;
}

const typeConfig: Record<NotificationType, TypeConfig> = {
  info: {
    icon: Info,
    bgColor: 'bg-info',
    borderColor: 'bg-light/50',
  },
  success: {
    icon: CheckCircle,
    bgColor: 'bg-positive',
    borderColor: 'bg-light/50',
  },
  error: {
    icon: AlertCircle,
    bgColor: 'bg-negative',
    borderColor: 'bg-light/50',
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'bg-warn',
    borderColor: 'bg-light/50',
  },
};

const showNotification = (
  type: NotificationType,
  { title, description, duration, onDismiss }: NotificationOptions,
) => {
  const config = typeConfig[type];
  const Icon = config.icon;
  const animationDuration = duration ?? 5000;

  return toast.custom(
    (t) => (
      <div
        className={`w-[25rem] ${config.bgColor} shadow-lg rounded-lg py-[1rem] px-[0.75rem] flex items-start gap-3 relative overflow-hidden`}
      >
        <div className="flex-shrink-0 mt-0.5">
          <Icon className={`text-light w-[1.5rem] h-[1.5rem]`} />
        </div>
        <div className="flex-1 flex flex-col gap-1">
          <div className="flex items-start justify-between">
            {title && <p className={`text-light font-semibold text-base`}>{title}</p>}
          </div>
          {description && <p className="text-light text-base leading-relaxed">{description}</p>}
        </div>
        <button
          type="button"
          className="text-secondary hover:text-secondary-hover cursor-pointer ml-2 flex-shrink-0"
          onClick={() => {
            toast.dismiss(t);
            if (onDismiss) {
              onDismiss();
            }
          }}
        >
          <X size={18} />
        </button>
        {/* Barra de progreso inferior */}
        <div
          className={`absolute bottom-0 left-0 h-[0.30rem] ${config.borderColor} rounded-bl-lg`}
          style={{
            animation: `shrink ${animationDuration}ms linear forwards`,
            width: '100%',
          }}
        />
        <style>{`
          @keyframes shrink {
            from {
              width: 100%;
            }
            to {
              width: 0%;
            }
          }
        `}</style>
      </div>
    ),
    {
      duration: animationDuration,
    },
  );
};

/**
 * Sistema de notificaciones toast usando Sonner
 *
 * @example
 * // Notificación de error
 * Notification.error({
 *   title: 'Error al guardar',
 *   description: 'No se pudo conectar con el servidor',
 * });
 *
 * @example
 * // Notificación de éxito con duración personalizada
 * Notification.success({
 *   title: 'Guardado exitoso',
 *   description: 'Los cambios se guardaron correctamente',
 *   duration: 3000,
 * });
 */
export const Notification = {
  info: (options: NotificationOptions) => showNotification('info', options),
  success: (options: NotificationOptions) => showNotification('success', options),
  error: (options: NotificationOptions) => showNotification('error', options),
  warning: (options: NotificationOptions) => showNotification('warning', options),
  show: (options: NotificationOptions) => showNotification('info', options), // default
};

// Re-exportar para uso directo si se necesita
export { toast } from 'sonner';
